// src/components/TeamManagementModal.jsx
import { useState, useEffect } from 'react';
import { useGraphQL } from '../graphql';
import Modal, { ModalConfirm } from './Modal';
import {
    UserGroupIcon,
    EnvelopeIcon,
    UserPlusIcon,
    Cog6ToothIcon,
    ArrowPathIcon,
    TrashIcon,
    CheckIcon,
    XMarkIcon,
    ExclamationTriangleIcon,
    PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

// Tier user limits
const TIER_USER_LIMITS = {
    BASIC: 1,
    PRO: 5,
    ENTERPRISE: 999
};

const getTierBadgeColor = (tier) => {
    switch (tier) {
        case 'BASIC': return 'bg-gray-600 text-gray-100';
        case 'PRO': return 'bg-blue-600 text-blue-100';
        case 'ENTERPRISE': return 'bg-purple-600 text-purple-100';
        default: return 'bg-gray-600 text-gray-100';
    }
};

const getStatusBadgeColor = (status) => {
    switch (status) {
        case 'PENDING': return 'bg-yellow-50 text-yellow-700';
        case 'ACCEPTED': return 'bg-green-50 text-green-700';
        case 'REJECTED': return 'bg-red-50 text-red-700';
        case 'EXPIRED': return 'bg-gray-700 text-gray-400';
        case 'CANCELLED': return 'bg-gray-700 text-gray-400';
        default: return 'bg-gray-700 text-gray-400';
    }
};

const getRoleBadgeColor = (role) => {
    switch (role) {
        case 'ADMIN': return 'bg-red-50 text-red-700';
        case 'MANAGER': return 'bg-blue-50 text-blue-700';
        case 'USER': return 'bg-gray-700 text-gray-300';
        default: return 'bg-gray-700 text-gray-300';
    }
};

const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const formatRelativeDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return formatDate(dateString);
};

const TeamManagementModal = ({ isOpen, onClose, company, onUpdate }) => {
    const { executeQuery, executeMutation, queries, mutations } = useGraphQL();
    const [activeTab, setActiveTab] = useState('members');
    const [teamInfo, setTeamInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState(null);
    const [actionResult, setActionResult] = useState(null);

    // Invite form state
    const [showInviteForm, setShowInviteForm] = useState(false);
    const [inviteData, setInviteData] = useState({ email: '', role: 'USER', message: '' });

    // Settings state
    const [newUserLimit, setNewUserLimit] = useState('');
    const [newRepUserId, setNewRepUserId] = useState('');

    // Confirmation modals
    const [confirmAction, setConfirmAction] = useState(null);

    const tier = company?.subscription?.tier || 'BASIC';
    const defaultLimit = TIER_USER_LIMITS[tier] || 1;

    useEffect(() => {
        if (isOpen && company) {
            loadTeamInfo();
        }
    }, [isOpen, company]);

    useEffect(() => {
        if (teamInfo) {
            setNewUserLimit(teamInfo.maxUsers?.toString() || defaultLimit.toString());
        }
    }, [teamInfo, defaultLimit]);

    const loadTeamInfo = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await executeQuery(queries.getAdminTeamInfo(company.id), {
                companyId: company.id
            });
            setTeamInfo(data?.adminTeamInfo || null);
        } catch (err) {
            console.error('Failed to load team info:', err);
            // If the query fails (field doesn't exist yet), use fallback data from company
            if (err.message?.includes('Cannot query field') || err.message?.includes('adminTeamInfo')) {
                setTeamInfo({
                    members: company.users || [],
                    memberCount: company.users?.length || 0,
                    maxUsers: company.maxUsers || defaultLimit,
                    canAddUsers: (company.users?.length || 0) < (company.maxUsers || defaultLimit),
                    pendingInvitations: [],
                    pendingReferrals: []
                });
            } else {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const showActionResultMessage = (message, isError = false) => {
        setActionResult({ message, isError });
        setTimeout(() => setActionResult(null), 5000);
    };

    // ==================== Member Actions ====================
    const handleRemoveMember = async (userId, userName) => {
        setConfirmAction({
            type: 'remove',
            title: 'Remove Team Member',
            message: `Are you sure you want to remove "${userName}" from this team? They will lose access to the company's resources.`,
            onConfirm: async () => {
                setActionLoading(true);
                try {
                    const result = await executeMutation(mutations.adminRemoveTeamMember, {
                        userId
                    });
                    if (result.removeTeamMember?.success) {
                        showActionResultMessage(`Successfully removed ${userName} from the team`);
                        await loadTeamInfo();
                        onUpdate?.();
                    } else {
                        showActionResultMessage(result.removeTeamMember?.error || 'Failed to remove team member', true);
                    }
                } catch (err) {
                    showActionResultMessage(err.message, true);
                } finally {
                    setActionLoading(false);
                    setConfirmAction(null);
                }
            }
        });
    };

    const handleTransferRepresentative = async () => {
        if (!newRepUserId) {
            showActionResultMessage('Please select a team member', true);
            return;
        }

        const newRep = teamInfo?.members?.find(m => m.id === newRepUserId);
        setConfirmAction({
            type: 'transfer',
            title: 'Transfer Representative Role',
            message: `Are you sure you want to transfer the company representative role to "${newRep?.firstName} ${newRep?.lastName}"? The current representative will lose their representative privileges.`,
            onConfirm: async () => {
                setActionLoading(true);
                try {
                    const result = await executeMutation(mutations.adminTransferRepresentative, {
                        newRepUserId
                    });
                    if (result.transferRepresentative?.success) {
                        showActionResultMessage(`Successfully transferred representative role to ${newRep?.firstName} ${newRep?.lastName}`);
                        setNewRepUserId('');
                        await loadTeamInfo();
                        onUpdate?.();
                    } else {
                        showActionResultMessage(result.transferRepresentative?.error || 'Failed to transfer representative', true);
                    }
                } catch (err) {
                    showActionResultMessage(err.message, true);
                } finally {
                    setActionLoading(false);
                    setConfirmAction(null);
                }
            }
        });
    };

    // ==================== Invitation Actions ====================
    const handleInviteMember = async (e) => {
        e.preventDefault();
        if (!inviteData.email) {
            showActionResultMessage('Please enter an email address', true);
            return;
        }

        setActionLoading(true);
        try {
            const result = await executeMutation(mutations.adminInviteTeamMember, {
                input: {
                    email: inviteData.email,
                    role: inviteData.role,
                    message: inviteData.message || undefined
                }
            });
            if (result.inviteTeamMember?.success) {
                showActionResultMessage(`Invitation sent to ${inviteData.email}`);
                setInviteData({ email: '', role: 'USER', message: '' });
                setShowInviteForm(false);
                await loadTeamInfo();
                onUpdate?.();
            } else {
                showActionResultMessage(result.inviteTeamMember?.error || 'Failed to send invitation', true);
            }
        } catch (err) {
            showActionResultMessage(err.message, true);
        } finally {
            setActionLoading(false);
        }
    };

    const handleCancelInvitation = async (invitationId, email) => {
        setConfirmAction({
            type: 'cancel',
            title: 'Cancel Invitation',
            message: `Are you sure you want to cancel the invitation to "${email}"?`,
            onConfirm: async () => {
                setActionLoading(true);
                try {
                    const result = await executeMutation(mutations.adminCancelInvitation, {
                        invitationId
                    });
                    if (result.cancelInvitation?.success) {
                        showActionResultMessage(`Invitation to ${email} cancelled`);
                        await loadTeamInfo();
                        onUpdate?.();
                    } else {
                        showActionResultMessage(result.cancelInvitation?.error || 'Failed to cancel invitation', true);
                    }
                } catch (err) {
                    showActionResultMessage(err.message, true);
                } finally {
                    setActionLoading(false);
                    setConfirmAction(null);
                }
            }
        });
    };

    // ==================== Referral Actions ====================
    const handleRespondToReferral = async (invitationId, approve, email) => {
        const action = approve ? 'approve' : 'reject';
        setConfirmAction({
            type: action,
            title: approve ? 'Approve Join Request' : 'Reject Join Request',
            message: approve
                ? `Are you sure you want to approve the join request from "${email}"? They will be added to the team.`
                : `Are you sure you want to reject the join request from "${email}"?`,
            onConfirm: async () => {
                setActionLoading(true);
                try {
                    const result = await executeMutation(mutations.adminRespondToJoinRequest, {
                        input: {
                            invitationId,
                            approve
                        }
                    });
                    if (result.respondToJoinRequest?.success) {
                        showActionResultMessage(approve ? `Approved join request from ${email}` : `Rejected join request from ${email}`);
                        await loadTeamInfo();
                        onUpdate?.();
                    } else {
                        showActionResultMessage(result.respondToJoinRequest?.error || `Failed to ${action} join request`, true);
                    }
                } catch (err) {
                    showActionResultMessage(err.message, true);
                } finally {
                    setActionLoading(false);
                    setConfirmAction(null);
                }
            }
        });
    };

    // ==================== Settings Actions ====================
    const handleSetUserLimit = async () => {
        const limitNum = parseInt(newUserLimit, 10);
        if (isNaN(limitNum) || limitNum < 1) {
            showActionResultMessage('Please enter a valid number (minimum 1)', true);
            return;
        }

        if (limitNum < (teamInfo?.memberCount || 0)) {
            showActionResultMessage(`Cannot set limit below current member count (${teamInfo?.memberCount})`, true);
            return;
        }

        setActionLoading(true);
        try {
            const result = await executeMutation(mutations.adminSetCompanyUserLimit, {
                companyId: company.id,
                maxUsers: limitNum
            });
            if (result.adminSetCompanyUserLimit?.success) {
                showActionResultMessage(`User limit updated to ${limitNum}`);
                await loadTeamInfo();
                onUpdate?.();
            } else {
                showActionResultMessage(result.adminSetCompanyUserLimit?.error || 'Failed to update user limit', true);
            }
        } catch (err) {
            showActionResultMessage(err.message, true);
        } finally {
            setActionLoading(false);
        }
    };

    // ==================== Tab Content Renderers ====================
    const renderMembersTab = () => {
        const members = teamInfo?.members || [];

        return (
            <div className="space-y-4">
                {/* Header with invite button */}
                <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-300">
                        Team Members ({teamInfo?.memberCount || 0}/{teamInfo?.maxUsers || defaultLimit})
                    </h4>
                    {teamInfo?.canAddUsers && (
                        <button
                            onClick={() => { setShowInviteForm(true); setActiveTab('invitations'); }}
                            className="flex items-center px-3 py-1.5 text-sm bg-primary text-white hover:bg-opacity-90"
                        >
                            <UserPlusIcon className="h-4 w-4 mr-1" />
                            Invite User
                        </button>
                    )}
                </div>

                {/* Capacity indicator */}
                <div className="bg-gray-700 p-3">
                    <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-gray-400">Team Capacity</span>
                        <span className="text-white">
                            {teamInfo?.memberCount || 0} / {teamInfo?.maxUsers || defaultLimit} users
                        </span>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-2">
                        <div
                            className={`h-2 rounded-full ${
 (teamInfo?.memberCount || 0) >= (teamInfo?.maxUsers || defaultLimit)
 ? 'bg-red-500'
 : (teamInfo?.memberCount || 0) / (teamInfo?.maxUsers || defaultLimit) > 0.8
 ? 'bg-yellow-500'
 : 'bg-primary'
 }`}
                            style={{ width: `${Math.min(100, ((teamInfo?.memberCount || 0) / (teamInfo?.maxUsers || defaultLimit)) * 100)}%` }}
                        />
                    </div>
                    {!teamInfo?.canAddUsers && (
                        <p className="text-xs text-yellow-700 mt-2">
                            User limit reached. Upgrade subscription or adjust limit in Settings.
                        </p>
                    )}
                </div>

                {/* Members list */}
                {members.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                        No team members found
                    </div>
                ) : (
                    <div className="space-y-2">
                        {members.map(member => (
                            <div
                                key={member.id}
                                className={`bg-gray-700 p-3 flex items-center justify-between ${
 member.isCompanyRepresentative ? 'ring-1 ring-yellow-500/50' : ''
 }`}
                            >
                                <div className="flex items-center space-x-3">
                                    <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center text-white font-medium">
                                        {member.firstName?.[0]}{member.lastName?.[0]}
                                    </div>
                                    <div>
                                        <div className="flex items-center space-x-2">
                                            <span className="text-white font-medium">
                                                {member.firstName} {member.lastName}
                                            </span>
                                            {member.isCompanyRepresentative && (
                                                <span className="flex items-center px-2 py-0.5 text-xs font-medium bg-yellow-50 text-yellow-700">
                                                    <StarIconSolid className="h-3 w-3 mr-1" />
                                                    Representative
                                                </span>
                                            )}
                                            <span className={`px-2 py-0.5 text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                                                {member.role}
                                            </span>
                                            {!member.active && (
                                                <span className="px-2 py-0.5 text-xs font-medium bg-red-50 text-red-700">
                                                    Inactive
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-400">
                                            {member.email}
                                            {member.jobTitle && ` • ${member.jobTitle}`}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            Last login: {formatRelativeDate(member.lastLogin)}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                    {!member.isCompanyRepresentative && (
                                        <button
                                            onClick={() => handleRemoveMember(member.id, `${member.firstName} ${member.lastName}`)}
                                            className="p-2 text-gray-400 hover:text-red-700 hover:bg-gray-600"
                                            title="Remove from team"
                                            disabled={actionLoading}
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderInvitationsTab = () => {
        const invitations = teamInfo?.pendingInvitations || [];

        return (
            <div className="space-y-4">
                {/* Invite form */}
                {showInviteForm ? (
                    <div className="bg-gray-700 p-4">
                        <h4 className="text-sm font-medium text-white mb-3">Send Invitation</h4>
                        <form onSubmit={handleInviteMember} className="space-y-3">
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Email Address *</label>
                                <input
                                    type="email"
                                    value={inviteData.email}
                                    onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                                    className="w-full bg-gray-600 border border-gray-500 text-white px-3 py-2"
                                    placeholder="user@example.com"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Role</label>
                                <select
                                    value={inviteData.role}
                                    onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                                    className="w-full bg-gray-600 border border-gray-500 text-white px-3 py-2"
                                >
                                    <option value="USER">User</option>
                                    <option value="MANAGER">Manager</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Message (optional)</label>
                                <textarea
                                    value={inviteData.message}
                                    onChange={(e) => setInviteData({ ...inviteData, message: e.target.value })}
                                    className="w-full bg-gray-600 border border-gray-500 text-white px-3 py-2"
                                    rows={2}
                                    placeholder="Welcome message..."
                                />
                            </div>
                            <div className="flex justify-end space-x-2">
                                <button
                                    type="button"
                                    onClick={() => { setShowInviteForm(false); setInviteData({ email: '', role: 'USER', message: '' }); }}
                                    className="px-3 py-1.5 text-sm bg-gray-600 text-white hover:bg-gray-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={actionLoading}
                                    className="flex items-center px-3 py-1.5 text-sm bg-primary text-white hover:bg-opacity-90 disabled:opacity-50"
                                >
                                    <PaperAirplaneIcon className="h-4 w-4 mr-1" />
                                    {actionLoading ? 'Sending...' : 'Send Invitation'}
                                </button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-300">
                            Pending Invitations ({invitations.length})
                        </h4>
                        {teamInfo?.canAddUsers && (
                            <button
                                onClick={() => setShowInviteForm(true)}
                                className="flex items-center px-3 py-1.5 text-sm bg-primary text-white hover:bg-opacity-90"
                            >
                                <UserPlusIcon className="h-4 w-4 mr-1" />
                                New Invitation
                            </button>
                        )}
                    </div>
                )}

                {/* Invitations list */}
                {invitations.length === 0 && !showInviteForm ? (
                    <div className="text-center py-8 text-gray-400">
                        <EnvelopeIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        No pending invitations
                    </div>
                ) : (
                    <div className="space-y-2">
                        {invitations.map(invitation => (
                            <div key={invitation.id} className="bg-gray-700 p-3 flex items-center justify-between">
                                <div>
                                    <div className="flex items-center space-x-2">
                                        <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                                        <span className="text-white">{invitation.inviteeEmail}</span>
                                        <span className={`px-2 py-0.5 text-xs font-medium ${getStatusBadgeColor(invitation.status)}`}>
                                            {invitation.status}
                                        </span>
                                        <span className={`px-2 py-0.5 text-xs font-medium ${getRoleBadgeColor(invitation.role)}`}>
                                            {invitation.role}
                                        </span>
                                    </div>
                                    <div className="text-sm text-gray-400 mt-1">
                                        Invited by {invitation.inviter?.firstName} {invitation.inviter?.lastName} • {formatRelativeDate(invitation.createdAt)}
                                    </div>
                                    {invitation.message && (
                                        <div className="text-sm text-gray-500 mt-1 italic">
                                            &ldquo;{invitation.message}&rdquo;
                                        </div>
                                    )}
                                    <div className="text-xs text-gray-500 mt-1">
                                        Expires: {formatDate(invitation.expiresAt)}
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleCancelInvitation(invitation.id, invitation.inviteeEmail)}
                                    className="p-2 text-gray-400 hover:text-red-700 hover:bg-gray-600"
                                    title="Cancel invitation"
                                    disabled={actionLoading}
                                >
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderReferralsTab = () => {
        const referrals = teamInfo?.pendingReferrals || [];

        return (
            <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-300">
                    Pending Join Requests ({referrals.length})
                </h4>

                {referrals.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                        <UserPlusIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        No pending join requests
                    </div>
                ) : (
                    <div className="space-y-2">
                        {referrals.map(referral => (
                            <div key={referral.id} className="bg-gray-700 p-3">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="flex items-center space-x-2">
                                            <span className="text-white font-medium">
                                                {referral.firstName} {referral.lastName}
                                            </span>
                                            <span className={`px-2 py-0.5 text-xs font-medium ${getStatusBadgeColor(referral.status)}`}>
                                                {referral.status}
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-400">{referral.inviteeEmail}</div>
                                        {referral.message && (
                                            <div className="text-sm text-gray-500 mt-1 italic bg-gray-800 p-2">
                                                &ldquo;{referral.message}&rdquo;
                                            </div>
                                        )}
                                        <div className="text-xs text-gray-500 mt-1">
                                            Requested {formatRelativeDate(referral.createdAt)} • Expires {formatDate(referral.expiresAt)}
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <button
                                            onClick={() => handleRespondToReferral(referral.id, true, referral.inviteeEmail)}
                                            className="flex items-center px-3 py-1.5 text-sm bg-green-600 text-white hover:bg-green-500"
                                            disabled={actionLoading || !teamInfo?.canAddUsers}
                                            title={!teamInfo?.canAddUsers ? 'User limit reached' : 'Approve'}
                                        >
                                            <CheckIcon className="h-4 w-4 mr-1" />
                                            Approve
                                        </button>
                                        <button
                                            onClick={() => handleRespondToReferral(referral.id, false, referral.inviteeEmail)}
                                            className="flex items-center px-3 py-1.5 text-sm bg-red-600 text-white hover:bg-red-500"
                                            disabled={actionLoading}
                                        >
                                            <XMarkIcon className="h-4 w-4 mr-1" />
                                            Reject
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const renderSettingsTab = () => {
        const members = teamInfo?.members || [];
        const nonRepMembers = members.filter(m => !m.isCompanyRepresentative);
        const currentRep = members.find(m => m.isCompanyRepresentative);

        return (
            <div className="space-y-6">
                {/* User Limit Override */}
                <div className="bg-gray-700 p-4">
                    <h4 className="text-sm font-medium text-white mb-3">User Limit Override</h4>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-400">Subscription Tier Limit:</span>
                            <span className="text-white">{defaultLimit} users ({tier})</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-400">Current Members:</span>
                            <span className="text-white">{teamInfo?.memberCount || 0} users</span>
                        </div>
                        <div className="flex items-center space-x-3">
                            <label className="text-sm text-gray-400 whitespace-nowrap">Custom Limit:</label>
                            <input
                                type="number"
                                min="1"
                                value={newUserLimit}
                                onChange={(e) => setNewUserLimit(e.target.value)}
                                className="w-24 bg-gray-600 border border-gray-500 text-white px-3 py-1.5 text-sm"
                            />
                            <button
                                onClick={handleSetUserLimit}
                                disabled={actionLoading || newUserLimit === (teamInfo?.maxUsers?.toString() || defaultLimit.toString())}
                                className="px-3 py-1.5 text-sm bg-primary text-white hover:bg-opacity-90 disabled:opacity-50"
                            >
                                {actionLoading ? 'Saving...' : 'Apply'}
                            </button>
                        </div>
                        <p className="text-xs text-gray-500">
                            Override the default user limit for this company. Set to the tier default ({defaultLimit}) to remove override.
                        </p>
                    </div>
                </div>

                {/* Representative Transfer */}
                <div className="bg-gray-700 p-4">
                    <h4 className="text-sm font-medium text-white mb-3">Representative Transfer</h4>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-400">Current Representative:</span>
                            <span className="text-white flex items-center">
                                <StarIconSolid className="h-4 w-4 text-yellow-700 mr-1" />
                                {currentRep ? `${currentRep.firstName} ${currentRep.lastName}` : 'None'}
                            </span>
                        </div>
                        {nonRepMembers.length > 0 ? (
                            <div className="flex items-center space-x-3">
                                <label className="text-sm text-gray-400 whitespace-nowrap">Transfer to:</label>
                                <select
                                    value={newRepUserId}
                                    onChange={(e) => setNewRepUserId(e.target.value)}
                                    className="flex-1 bg-gray-600 border border-gray-500 text-white px-3 py-1.5 text-sm"
                                >
                                    <option value="">Select team member...</option>
                                    {nonRepMembers.map(member => (
                                        <option key={member.id} value={member.id}>
                                            {member.firstName} {member.lastName} ({member.email})
                                        </option>
                                    ))}
                                </select>
                                <button
                                    onClick={handleTransferRepresentative}
                                    disabled={actionLoading || !newRepUserId}
                                    className="px-3 py-1.5 text-sm bg-yellow-600 text-white hover:bg-yellow-500 disabled:opacity-50"
                                >
                                    {actionLoading ? 'Transferring...' : 'Transfer'}
                                </button>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500">
                                No other team members available for representative transfer.
                            </p>
                        )}
                        <div className="flex items-start space-x-2 text-xs text-yellow-700 bg-yellow-50 p-2">
                            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <p>
                                The representative can manage team members, invitations, and company settings.
                                This action will immediately transfer all representative privileges.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (!company) return null;

    const tabs = [
        { id: 'members', label: 'Members', icon: UserGroupIcon, count: teamInfo?.memberCount },
        { id: 'invitations', label: 'Invitations', icon: EnvelopeIcon, count: teamInfo?.pendingInvitations?.length },
        { id: 'referrals', label: 'Join Requests', icon: UserPlusIcon, count: teamInfo?.pendingReferrals?.length },
        { id: 'settings', label: 'Settings', icon: Cog6ToothIcon }
    ];

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={`Team Management: ${company.name}`}
                size="xl"
            >
                <div className="space-y-4">
                    {/* Company/Tier info header */}
                    <div className="bg-gray-700 p-4 flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div>
                                <span className="text-gray-300">Subscription:</span>
                                <span className={`ml-2 px-3 py-1 text-sm font-medium ${getTierBadgeColor(tier)}`}>
                                    {tier}
                                </span>
                            </div>
                            <div className="text-sm text-gray-400">
                                Default limit: {defaultLimit} user{defaultLimit !== 1 ? 's' : ''}
                            </div>
                        </div>
                        <div className="text-sm">
                            <span className="text-gray-400">Team: </span>
                            <span className="text-white font-medium">
                                {teamInfo?.memberCount || 0}/{teamInfo?.maxUsers || defaultLimit}
                            </span>
                            {teamInfo?.maxUsers && teamInfo.maxUsers !== defaultLimit && (
                                <span className="ml-1 text-xs text-purple-700">(custom)</span>
                            )}
                        </div>
                    </div>

                    {/* Action Result */}
                    {actionResult && (
                        <div className={`${actionResult.isError ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'} border p-3`}>
                            <p className={actionResult.isError ? 'text-red-700' : 'text-green-700'}>
                                {actionResult.message}
                            </p>
                        </div>
                    )}

                    {/* Error */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 p-3">
                            <p className="text-red-700">Error: {error}</p>
                        </div>
                    )}

                    {/* Tabs */}
                    <div className="border-b border-gray-700">
                        <nav className="flex space-x-1">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center px-4 py-2 text-sm font-medium transition-colors ${
 activeTab === tab.id
 ? 'bg-gray-700 text-white border-b-2 border-primary'
 : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
 }`}
                                >
                                    <tab.icon className="h-4 w-4 mr-2" />
                                    {tab.label}
                                    {tab.count !== undefined && tab.count > 0 && (
                                        <span className={`ml-2 px-1.5 py-0.5 text-xs rounded-full ${
 activeTab === tab.id ? 'bg-primary text-white' : 'bg-gray-600 text-gray-300'
 }`}>
                                            {tab.count}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Tab content */}
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
                        </div>
                    ) : (
                        <div className="max-h-[50vh] overflow-y-auto">
                            {activeTab === 'members' && renderMembersTab()}
                            {activeTab === 'invitations' && renderInvitationsTab()}
                            {activeTab === 'referrals' && renderReferralsTab()}
                            {activeTab === 'settings' && renderSettingsTab()}
                        </div>
                    )}
                </div>
            </Modal>

            {/* Confirmation Modal */}
            <ModalConfirm
                isOpen={!!confirmAction}
                onClose={() => setConfirmAction(null)}
                onConfirm={confirmAction?.onConfirm}
                title={confirmAction?.title}
                message={confirmAction?.message}
                confirmLabel={confirmAction?.type === 'remove' || confirmAction?.type === 'reject' ? 'Remove' : 'Confirm'}
                type={confirmAction?.type === 'remove' || confirmAction?.type === 'reject' || confirmAction?.type === 'cancel' ? 'danger' : 'warning'}
            />
        </>
    );
};

export default TeamManagementModal;
